import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, Modal, OutlinedInput, Paper, Switch, TextField } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../services/api';
import MultipleSelect from '../components/Multiselect';

function UserForm() {
  const { username } = useParams();
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    isActive: true,
    companies: [],
  });
  const [formPasswordData, setFormPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);

  const handleClickShowNewPassword = () => setShowNewPassword((show) => !show);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isMounted) {
        await getAllCompanies();
        if (username) {
          await getUserByUsername(username);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const navigateBack = () => navigate('/usuarios');

  const getAllCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data.data);
    } catch (err) {
      setCompanies([]);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const updateUser = async (username, data) => {
    try {
      setLoading(true);
      await api.patch(`/users/${username}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      navigateBack();
    } catch (err) {
      setLoading(false);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const updateUserPassword = async (username, data) => {
    try {
      setLoading(true);
      await api.patch(`/users/password/${username}`, data, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      handleClose();
    } catch (err) {
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    } finally {
      setLoading(false);
    }
  };

  const createNewUser = async (data) => {
    try {
      setLoading(true);
      await api.post('/users/register', data);
      navigateBack();
    } catch (err) {
      setLoading(false);
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const getUserByUsername = async (username) => {
    try {
      const response = await api.get(`/users/${username}`);
      const actualCompanies = response.data.data.UserCompanies.map(({ Company: { id, name } }) => ({ id, name }));
      delete response.data.data.UserCompanies;
      setFormData({
        ...response.data.data,
        companies: actualCompanies,
      });
    } catch (err) {
      showError(err.response?.data?.message || 'Algo de errado aconteceu.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangePasswordForm = (e) => {
    const { name, value } = e.target;
    setFormPasswordData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const handleSubmit = async () => {
    const data = {
      name: formData.name,
      username: formData.username,
      password: formData.password,
      isActive: formData.isActive,
      companies: formData.companies.map(({ id }) => id),
    };

    if (username) {
      delete data.password;
      await updateUser(username, data);
    } else {
      await createNewUser(data);
    }
  };

  const handleSubmitPassword = async () => {
    const data = {
      oldPassword: formPasswordData.oldPassword,
      newPassword: formPasswordData.newPassword,
    };

    if (username) {
      await updateUserPassword(username, data);
    }
  };

  const showError = (message) => {
    const alert = <Alert
      className="error-content"
      variant="outlined"
      severity="error"
    >
      {message}
    </Alert>;
    setError(alert);
    setTimeout(() => setError(null), 3000);
  };

  const handleOpen = () => {
    setOpen(true);
  }

  const handleClose = () => {
    setOpen(false);
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '464px',
    width: '70%',
    height: '400px',
    backgroundColor: '#1E1E1E',
    border: 'none',
    padding: '2rem',
    borderRadius: '4px',
  };

  return (
    <div>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <>
          {error}
          <Box style={style}>
            <div className="flex-column gap-05">
              <h2 style={{ margin: '0 0 1rem' }}>Alterar senha</h2>
              <FormControl sx={{ width: '100%' }} variant="outlined">
                <InputLabel
                  name="oldPassword"
                  size="small"
                  htmlFor="outlined-adornment-password"
                  onChange={handleChangePasswordForm}
                >
                  Senha antiga
                </InputLabel>
                <OutlinedInput
                  label="Senha antiga"
                  size="small"
                  name="oldPassword"
                  id="oldPassword"
                  disabled={loading}
                  onChange={handleChangePasswordForm}
                  type={showOldPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        title={
                          showOldPassword ? 'Ocultar senha' : 'Mostrar senha'
                        }
                        onClick={handleClickShowOldPassword}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
              <FormControl sx={{ width: '100%' }} variant="outlined">
                <InputLabel
                  name="newPassword"
                  size="small"
                  htmlFor="outlined-adornment-password"
                  onChange={handleChangePasswordForm}
                >
                  Nova senha
                </InputLabel>
                <OutlinedInput
                  label="Nova senha"
                  size="small"
                  name="newPassword"
                  id="newPassword"
                  disabled={loading}
                  onChange={handleChangePasswordForm}
                  type={showNewPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        title={
                          showNewPassword ? 'Ocultar senha' : 'Mostrar senha'
                        }
                        onClick={handleClickShowNewPassword}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </div>
            <div id="modal-password" className="align-center" style={{ position: 'absolute', bottom: '2rem' }}>
              <Button
                sx={{ width: '150px', height: '40px', marginRight: '.5rem' }}
                variant="outlined"
                color="error"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                sx={{ width: '150px', height: '40px' }}
                variant="outlined"
                color="success"
                onClick={handleSubmitPassword}
                disabled={loading}
              >
                { loading ? <CircularProgress size={20} /> : 'Salvar' }
              </Button>
            </div>
          </Box>
        </>
      </Modal>
      <div style={{ margin: '21.44px 0'}}>
        <Link className="color-white" to="/usuarios">Usuários</Link>
        <span> / </span>
        <h1 className="inline">{ username ? 'Editar' : 'Novo' }</h1>
      </div>
      <div className="w-100 h-100 align-center justify-center">
        {error}
        <Paper sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: '2rem',
          gap: '2rem',
        }}>
          <div
            className="w-100"
            style={{
              display: 'flex',
              gap: '.5rem',
              flexDirection: 'column',
            }}
          >
            <div className="w-100 align-center justify-between">
              <h3 style={{ margin: 0 }}>Dados usuário</h3>
              <FormControlLabel
                control={<Switch
                  disabled={loading}
                  name="isActive"
                  onChange={handleSwitchChange}
                  checked={formData.isActive}
                  label=""
                  labelPlacement="left"
                />}
                label="Usuário ativo"
                labelPlacement="start"
              />
            </div>
            <div
              className="w-100"
              style={{
                display: 'flex',
                gap: '.5rem',
              }}
            >
              <TextField
                sx={{ width: '100%' }}
                size="small"
                id="username"
                name="username"
                label="Nome de usuário"
                variant="outlined"
                disabled={loading}
                onChange={handleChange}
                value={formData.username}
              />
              <TextField
                sx={{ width: '100%' }}
                size="small"
                id="name"
                name="name"
                label="Nome"
                variant="outlined"
                disabled={loading}
                onChange={handleChange}
                value={formData.name}
              />
            </div>
            <div
              className="w-100"
              style={{
                display: 'flex',
                gap: '.5rem',
              }}
            >
              { !username ?
                <FormControl sx={{ width: '100%' }} variant="outlined">
                  <InputLabel
                    sx={{ width: '100%' }}
                    name="password"
                    size="small"
                    htmlFor="outlined-adornment-password"
                    onChange={handleChange}
                  >
                    Senha
                  </InputLabel>
                  <OutlinedInput
                    sx={{ width: '100%' }}
                    label="Senha"
                    size="small"
                    name="password"
                    id="password"
                    disabled={loading}
                    onChange={handleChange}
                    type={showPassword ? 'text' : 'password'}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          title={
                            showPassword ? 'Ocultar senha' : 'Mostrar senha'
                          }
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </FormControl> : 
                <div className="align-center" style={{ width: '100%' }}>
                  <span onClick={handleOpen} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                    Alterar senha
                  </span>
                </div>
              }
            </div>
          </div>
          <div
            className="w-100"
            style={{
              display: 'flex',
              gap: '.5rem',
              flexDirection: 'column',
            }}
          >
            <h3 style={{ margin: 0 }}>Responsabilidade</h3>
            <div
              className="w-100"
              style={{
                display: 'flex',
                gap: '.5rem',
              }}
            >
              <MultipleSelect
                onChange={(value) => handleChange({ target: { name: 'companies', value } })}
                label="Empresas"
                description="name"
                loading={loading}
                data={companies}
                defaultData={formData.companies}
              />
            </div>
          </div>
          <div className="w-100">
            <Button
              sx={{ width: '150px', height: '40px', marginRight: '.5rem' }}
              variant="outlined"
              color="error"
              onClick={navigateBack}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              sx={{ width: '150px', height: '40px' }}
              variant="outlined"
              color="success"
              onClick={handleSubmit}
              disabled={loading}
            >
              { loading ? <CircularProgress size={20} /> : username ? 'Salvar' : 'Cadastrar' }
            </Button>
          </div>
        </Paper>
      </div>
    </div>
  );
}

export default UserForm;
